/**
 * The history of a report's commercial edits, and whether changing a
 * completed report's figures could touch money that has already been billed.
 *
 * `GET /reports/{id}/commercial-changes` returns one row per field that
 * changed (who, from what, to what, when, and — for edits made after the
 * report completed — why). `GET /reports/{id}/billing-status` backs the
 * warning shown before such an edit.
 */

/** A reason shorter than this is no reason (mirrors the backend rule). */
export const MIN_REASON_LENGTH = 10;

/**
 * Fields whose change on a COMPLETED report needs a reason — money and date
 * fields only. The case name and CX lead are recorded but never need one.
 * Mirrors `REASON_REQUIRED_FIELDS` in the backend's commercial change
 * service; the server enforces it, this only lets the UI ask up front.
 */
export const REASON_REQUIRED_FIELDS: ReadonlySet<string> = new Set([
  'price',
  'currency',
  'due_date',
  'turnaround_time_days',
  'is_proof_of_concept',
  'is_research_and_development',
  'is_excluded_from_billing',
  'np_fee',
  'np_currency',
  'np_due_date',
  'np_turnaround_time_days',
]);

/** How each recorded field is named to a person. */
export const COMMERCIAL_FIELD_LABELS: Readonly<Record<string, string>> = {
  price: 'Price',
  currency: 'Currency',
  due_date: 'Due date',
  turnaround_time_days: 'Turnaround',
  is_proof_of_concept: 'Proof of concept',
  is_research_and_development: 'R&D',
  is_excluded_from_billing: 'Excluded from billing',
  cx_lead: 'CX lead',
  name: 'Case name',
  jurisdictions: 'Jurisdictions',
  np_fee: 'Partner fee',
  np_currency: 'Partner currency',
  np_due_date: 'Partner deadline',
  np_turnaround_time_days: 'Partner turnaround',
  fee_adjustment: 'Fee adjustment',
  fee_adjustment_note: 'Fee adjustment note',
};

export function commercialFieldLabel(field: string): string {
  return COMMERCIAL_FIELD_LABELS[field] ?? field;
}

/** Whether an edit that changes these fields needs a reason on a completed report. */
export function needsReason(changedFields: Iterable<string>): boolean {
  for (const field of changedFields) {
    if (REASON_REQUIRED_FIELDS.has(field)) {
      return true;
    }
  }
  return false;
}

/** A reason that is long enough once trimmed. */
export function isValidReason(reason: string | null | undefined): boolean {
  return (reason ?? '').trim().length >= MIN_REASON_LENGTH;
}

// --- History ---

export interface CommercialChange {
  id: string;
  reportId: string;
  /** Set for a partner-side change (the fee / deadline of one assignment). */
  assignmentId: string | null;
  field: string;
  oldValue: string | null;
  newValue: string | null;
  /** Only present for edits made after the report was completed. */
  reason: string | null;
  actorUserId: string | null;
  createdAt: string | null;
}

export interface ApiCommercialChange {
  id: string;
  report_id: string;
  assignment_id?: string | null;
  field: string;
  old_value?: string | null;
  new_value?: string | null;
  reason?: string | null;
  actor_user_id?: string | null;
  created_at?: string | null;
}

export interface ApiCommercialChangesResponse {
  changes: ApiCommercialChange[];
}

export function transformToCommercialChange(api: ApiCommercialChange): CommercialChange {
  return {
    id: api.id,
    reportId: api.report_id,
    assignmentId: api.assignment_id ?? null,
    field: api.field,
    oldValue: api.old_value ?? null,
    newValue: api.new_value ?? null,
    reason: api.reason ?? null,
    actorUserId: api.actor_user_id ?? null,
    createdAt: api.created_at ?? null,
  };
}

/**
 * The most recent change per field — what "last changed by … on …" shows
 * beside a value. `changes` is newest first (as the API returns it); pass an
 * `assignmentId` to read one partner assignment's fields, or leave it out for
 * the report's own.
 */
export function lastChangeByField(
  changes: readonly CommercialChange[],
  assignmentId: string | null = null
): Record<string, CommercialChange> {
  const latest: Record<string, CommercialChange> = {};
  for (const change of changes) {
    if (change.assignmentId === assignmentId && !(change.field in latest)) {
      latest[change.field] = change;
    }
  }
  return latest;
}

/** The most recent of several changes — for a line that covers more than one
 * field, such as the deadline (a fixed date or a turnaround). */
export function latestOf(
  ...changes: (CommercialChange | null | undefined)[]
): CommercialChange | null {
  let latest: CommercialChange | null = null;
  for (const change of changes) {
    if (change && (!latest || (change.createdAt ?? '') > (latest.createdAt ?? ''))) {
      latest = change;
    }
  }
  return latest;
}

// --- Billed status ---

export type BillingStatusSource = 'backoffice' | 'estimate' | 'no_project';

export interface AssignmentBilled {
  assignmentId: string;
  billingMonth: number;
  billingYear: number;
}

export interface ReportBillingStatus {
  completed: boolean;
  completedAt: string | null;
  /** `backoffice` is read from the billing records; `estimate` is a guess
   * from the completion month (Backoffice could not say); `no_project` means
   * nothing can have been billed. */
  source: BillingStatusSource;
  mayHaveBeenBilled: boolean;
  clientBilled: boolean | null;
  clientBillingMonth: number | null;
  clientBillingYear: number | null;
  assignmentsBilled: AssignmentBilled[];
}

export interface ApiReportBillingStatus {
  completed: boolean;
  completed_at?: string | null;
  source: BillingStatusSource;
  may_have_been_billed: boolean;
  client_billed?: boolean | null;
  client_billing_month?: number | null;
  client_billing_year?: number | null;
  assignments_billed?: { assignment_id: string; billing_month: number; billing_year: number }[];
}

export function transformToReportBillingStatus(api: ApiReportBillingStatus): ReportBillingStatus {
  return {
    completed: api.completed,
    completedAt: api.completed_at ?? null,
    source: api.source,
    mayHaveBeenBilled: api.may_have_been_billed,
    clientBilled: api.client_billed ?? null,
    clientBillingMonth: api.client_billing_month ?? null,
    clientBillingYear: api.client_billing_year ?? null,
    assignmentsBilled: (api.assignments_billed ?? []).map((row) => ({
      assignmentId: row.assignment_id,
      billingMonth: row.billing_month,
      billingYear: row.billing_year,
    })),
  };
}
